import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { setUnauthorizedHandler } from '../lib/api.js';
import { fetchMe } from './api.js';
import { LoginScreen } from './LoginScreen.js';

type State = 'checking' | 'signed-out' | 'signed-in' | 'expired';

/**
 * MAP-032. Once signed in, children stay mounted for the rest of the session:
 * an expiry draws an overlay over them rather than unmounting the app, so a
 * half-typed capture survives re-authentication.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>('checking');

  useEffect(() => {
    let cancelled = false;
    fetchMe().then(
      () => !cancelled && setState('signed-in'),
      () => !cancelled && setState((s) => (s === 'signed-in' ? 'expired' : 'signed-out')),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setState((s) => (s === 'signed-in' ? 'expired' : s));
    });
  }, []);

  const onSignedIn = useCallback(() => setState('signed-in'), []);

  if (state === 'checking') {
    return <div className="flex h-full items-center justify-center text-text-muted">…</div>;
  }

  if (state === 'signed-out') {
    return <LoginScreen onSignedIn={onSignedIn} />;
  }

  return (
    <>
      {children}
      {state === 'expired' && (
        <div
          className="fixed inset-0 z-50"
          style={{ background: 'color-mix(in oklch, var(--space-deep) 70%, transparent)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Session expired"
        >
          <LoginScreen onSignedIn={onSignedIn} resuming />
        </div>
      )}
    </>
  );
}
